import GetAppIcon from '@material-ui/icons/GetApp';
import DoneIcon from '@material-ui/icons/Done';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import UpdateIcon from '@material-ui/icons/Update';
import PublicIcon from '@material-ui/icons/Public';
import TuneIcon from '@material-ui/icons/Tune';
import RefreshIcon from '@material-ui/icons/Refresh';
import PermCameraMicIcon from '@material-ui/icons/PermCameraMic';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import PhonelinkSetupIcon from '@material-ui/icons/PhonelinkSetup';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';

import { getOsSupportedBrowsers, navigatorDetails } from 'UTILS/navigator';

export const getBrowserUpdateSupportLink = ({ browser = navigatorDetails.browser, os = navigatorDetails.os } = {}) => {
  switch (browser) {
    case 'firefox':
      return 'firefoxUpdateSupportLink';
    case 'safari':
      if (os === 'iOS') {
        return 'safariMobileUpdateSupportLink';
      }
      return 'safariDesktopUpdateSupportLink';
    case 'edge':
      return 'edgeUpdateSupportLink';
    default:
    case 'chrome':
      if (os === 'Android') {
        return 'chromeMobileUpdateSupportLink';
      }
      return 'chromeDesktopUpdateSupportLink';
  }
};

export const getBrowserUpdateSupportText = ({ os = navigatorDetails.os } = {}) => {
  switch (os) {
    case 'Android':
      return 'youJustNeedToUpdateYourChromeApp-clickBelowToSeeMoreDetailsAboutHowYouCanAchieveThis';
    case 'iOS':
      return 'noteThatYouNeedIOS13AreToBeAbleToUpdateSafari-clickBelowToSeeMoreDetailsAboutHowYouCanUpdateSafari';
    default:
      return 'mostBrowsersOfferAutoUpdateFeatures-ifYouDidNotRestartYourBrowserForALongTimeTryToDoIt-clickBelowToSeeMoreDetailsAboutHowYouCanUpdateYourBrowser';
  }
};

export const getOsSupportedBrowserList = ({ os = navigatorDetails.os } = {}) => {
  return getOsSupportedBrowsers(os).join('-');
};

export const getSupportedBrowserDownloadButtonList = ({ os = navigatorDetails.os } = {}) => {
  const browserDownloadLink = {
    chrome: 'chromeBrowserDownloadLink',
    firefox: 'firefoxBrowserDownloadLink',
    safari: 'safariBrowserDownloadLink',
    edge: 'edgeBrowserDownloadLink',
  };
  return getOsSupportedBrowsers(os).map(browser => ({
    href: browserDownloadLink[browser],
    label: 'downloadBrowser',
  }));
};

export const getBrowserPermissionSupportLink = ({ browser = navigatorDetails.browser, os = navigatorDetails.os } = {}) => {
  switch (browser) {
    case 'firefox':
      return 'firefoxPermissionSupportLink';
    case 'safari':
      return 'safariPermissionSupportLink';
    case 'edge':
      return 'edgePermissionSupportLink';
    case 'chrome':
    default:
      if (os === 'Android') {
        return 'chromeMobilePermissionSupportLink';
      }
      return 'chromeDesktopPermissionSupportLink';
  }
};

export const getBrowserPermissionSavingText = ({ browser = navigatorDetails.browser, os = navigatorDetails.os } = {}) => {
  switch (browser) {
    case 'firefox':
      return 'ifYouNeverDidItBeforeOnceYouHaveClickedTheGotItButtonTheBrowserItWillPromptForYourPermissionToUseYourDevice-youNeedToGiveYourPermissionToContinue-weHighlyRecommendYouToSaveThisPreferenceByTickingTheCheckBoxInTheDisplayedWindow';
    case 'safari':
      if (os === 'iOS') {
        return 'ifYouNeverDidItBeforeOnceYouHaveClickedTheGotItButtonTheBrowserItWillPromptForYourPermissionToUseYourDevice-youNeedToGiveYourPermissionToContinue';
      }
      return 'ifYouNeverDidItBeforeOnceYouHaveClickedTheGotItButtonTheBrowserItWillPromptForYourPermissionToUseYourDevice-youNeedToGiveYourPermissionToContinue-weHighlyRecommendYouToSaveThisPreferenceByTickingTheCheckBoxInTheDisplayedWindow';
    case 'edge':
      return 'ifYouNeverDidItBeforeOnceYouHaveClickedTheGotItButtonTheBrowserItWillPromptForYourPermissionToUseYourDevice-youNeedToGiveYourPermissionToContinue-weHighlyRecommendYouToSaveThisPreferenceForLaterByClickingOnTheRedCameraIconInTheSearchBarDuringTheTests';
    case 'chrome':
    default:
      if (os === 'Android') {
        return 'ifYouNeverDidItBeforeOnceYouHaveClickedTheGotItButtonTheBrowserItWillPromptForYourPermissionToUseYourDevice-youNeedToGiveYourPermissionToContinue';
      }
      return 'ifYouNeverDidItBeforeOnceYouHaveClickedTheGotItButtonTheBrowserItWillPromptForYourPermissionToUseYourDevice-youNeedToGiveYourPermissionToContinue-weHighlyRecommendYouToSaveThisPreferenceByTickingTheCheckBoxInTheDisplayedWindow';
  }
};

export const BROWSER_RESULT_MAP = {
  'browser_not_chrome_on_android': {
    message: 'yourBrowserIsNotSupportedOnAndroid',
    title: 'whichBrowserShouldIUseOnAndroid',
    hintList: [
      {
        icon: GetAppIcon,
        title: 'chrome',
        text: 'onlyChromeIsSupportedOnAndroid-youNeedToDownloadItFromThePlayStoreAndOpenTheLinkInChrome',
        buttonList: [{
          href: 'https://play.google.com/store/apps/details?id=com.android.chrome',
          label: 'downloadChrome',
        }],
      },
      {
        icon: DoneIcon,
        title: 'thePageDoesNotOpenInChrome',
        text: 'chromeIsNotNecessarilyYourDefaultBrowser-youMayNeedToChangeYourPhoneParameters',
        buttonList: [{
          href: 'chromeDefaultBrowserLink',
          label: 'stepByStep',
        }],
      },
      {
        icon: PhonelinkSetupIcon,
        title: 'itStillDoesNotWork',
        text: 'someTimesLinksAreOpenedInAppBrowserEvenIfChromeIsTheDefaultBrowser-youCanDisableThisBehaviorFromTheAppSettingsOrClickOnButtonInOptions',
      },
    ],
  },
  'browser_not_safari_on_iOS': {
    message: 'yourBrowserIsNotSupportedOnIOs',
    title: 'whichBrowserShouldIUseOnIOs',
    hintList: [
      {
        icon: GetAppIcon,
        title: 'safari',
        text: 'onlySafariIsSupportedOnIOs-itIsAlwaysInstalledOnIOsDevices-youNeedToOpenTheLinkInSafari',
      },
      {
        icon: DoneIcon,
        title: 'thePageDoesNotOpenInSafari',
        text: 'safariIsNotNecessarilyYourDefaultBrowser-youMayNeedToChangeYourPhoneParameters',
        buttonList: [{
          href: 'safariDefaultBrowserLink',
          label: 'stepByStep',
        }],
      },
      {
        icon: PhonelinkSetupIcon,
        title: 'itStillDoesNotWork',
        text: 'someTimesLinksAreOpenedInAppBrowserEvenIfSafariIsTheDefaultBrowser-youCanDisableThisBehaviorFromTheAppSettingsOrClickOnButtonInOptions',
      },
    ],
  },
  'browser_outdated': {
    message: 'yourBrowserVersionIsOutdated',
    title: 'howToUpdateMyBrowser',
    hintList: [
      {
        icon: TrendingUpIcon,
        title: 'whyShouldIUpdate',
        text: 'theVideoConferenceFeaturesUseCuttingEdgeTechnologiesThatAreOnlyAvailableOnModernBrowsers-inOrderToLetYouHaveTheBestPossibleVideoConferencingExperienceWeRecommendYouToUseTheMostRecentVersionOfYourBrowser',
      },
      {
        icon: UpdateIcon,
        title: 'restartTheBrowser',
        text: getBrowserUpdateSupportText(),
        buttonList: [{
          href: getBrowserUpdateSupportLink(),
          label: 'stepByStep',
        }],
      },
      {
        icon: SettingsApplicationsIcon,
        title: 'itStillDoesNotWork',
        text: 'theAutoUpdateFeatureMayBeDisabled-tryReEnablingItInTheApplicationSettingsOrInTheStoreSettings',
      },
    ],
  },
  'browser_not_supported': {
    message: 'yourBrowserCanNotPerformVideoConferences',
    title: 'whichBrowserDoINeedToPerformVideoConferences',
    hintList: [
      {
        icon: PublicIcon,
        title: getOsSupportedBrowserList(),
        text: 'everyOtherBrowserIsNotSupportedOnYourDevice',
        buttonList: getSupportedBrowserDownloadButtonList(),
      },
    ],
  },
  'browser_not_meeting_requirements': {
    message: 'yourBrowserCanNotPerformVideoConferences',
    title: 'whatCanIDoToPerformVideoConference',
    hintList: [
      {
        icon: TuneIcon,
        title: 'checkTheBrowserSettingsOrReinstall',
        text: 'ifYouAreInAnInstitutionAskTheITDeptTheyMayHaveAddedSecuritySettingsToTheBrowserOrDeviceThatPreventWebVideoConferences-ifNotTryReinstallingTheBrowser',
        buttonList: getSupportedBrowserDownloadButtonList(),
      },
    ],
  },
  'browser_supported': {
    message: 'yourBrowserCanPerformVideoConferences',
  },
  'permissions_not_given': {
    message: 'youNeedToAllowTheBrowserToAccessYourCameraAndMicrophone',
    title: 'allowingTheBrowserToAccessMyCameraAndMicrophone',
    hintList: [
      {
        icon: LiveHelpIcon,
        title: 'whyDoesTheBrowserNeedToAccessMyDevices',
        text: 'yourBrowserIsNotAllowedToAccessYourCameraAndMicrophoneWithoutYourPermission-yourBrowserNeedsPermissionToVerifyThatYourEquipmentSupportsVideoConferencing',
      },
      {
        icon: RefreshIcon,
        title: 'refreshBrowserPage',
        text: 'refreshThePageSoThatBrowserAsksForThePermissionsAgain-whenTheBrowserShowsAWindowToAskForYourPermissionToUseYourCameraAndMicrophoneYouNeedToAccept',
        buttonList: [{
          href: document.location.href,
          label: 'refresh',
          self: true,
        }],
      },
      {
        icon: PermCameraMicIcon,
        title: 'theBrowserDoesNotAskForPermissions',
        text: 'itProbablyMeansThatYouRejectedThePermissionInTheFirstPlace-youCanStillGivethePermissionsInTheBrowsersSettings',
        buttonList: [{
          href: getBrowserPermissionSupportLink(),
          label: 'stepByStep',
        }],
      },
    ],
  },
  'permissions_help': {
    message: 'youNeedToAllowTheBrowserToAccessYourCameraAndMicrophone',
    title: 'allowingTheBrowserToAccessMyCameraAndMicrophone',
    hintList: [
      {
        icon: PermCameraMicIcon,
        title: 'whyDoesTheBrowserNeedToAccessMyDevices',
        text: 'yourBrowserIsNotAllowedToAccessYourCameraAndMicrophoneWithoutYourPermission-yourBrowserNeedsPermissionToVerifyThatYourEquipmentSupportsVideoConferencing',
      },
      {
        icon: CheckBoxIcon,
        title: 'howToAllowBrowserToAccessMyDevices',
        text: getBrowserPermissionSavingText(),
        buttonList: [{
          href: getBrowserPermissionSupportLink(),
          label: 'stepByStep',
        }],
      },
    ],
  },
};
